import { useRouter } from "vue-router";
import { apiCallWithRetry } from "@/api/api-call-with-retry";
import { authenticateFacilityBooking } from "@/api/FacilityService";
import { checkRoomNumber } from "@/api/GepService";
import { useConfigStore } from "@/stores/store.config";
import {
  buildFacilityBookingUrl,
  getFacilityCategoryId,
  isTrustedFacilityBookingUrl,
} from "@/utils/facilityBookingUrl";
import { isRoomFallbackEnabled } from "@/utils/roomFallbackFeature";
import { requestRoomVerification } from "@/utils/roomVerificationFlow";
import { isRoomInvalid } from "@/utils/roomVerificationState";

export type FacilityBookingLaunchResult = "launched" | "untrusted" | "failed";

/**
 * Native counterpart of the legacy facility action. Facility booking links are
 * authenticated and always replace the current page.
 */
export function useFacilityBookingLauncher() {
  const router = useRouter();
  const config = useConfigStore();
  const user = config.useUserStore();

  const launchFacilityBooking = async (rawUrl: string, lang: string) => {
    if (!isTrustedFacilityBookingUrl(rawUrl)) return "untrusted";
    const categoryId = getFacilityCategoryId(rawUrl);
    if (!categoryId) return "untrusted";

    try {
      const secureRoomVerificationEnabled =
        user.homepageConfig?.secureRoomVerificationEnabled !== false;
      if (
        secureRoomVerificationEnabled &&
        isRoomFallbackEnabled() &&
        isRoomInvalid(config.apikey, user.merchantId || "")
      ) {
        await requestRoomVerification({ lang });
        return "failed";
      }
      if (secureRoomVerificationEnabled && user.genericQRCode !== true) {
        const roomCheck = await apiCallWithRetry(checkRoomNumber, {
          token: user.token || "",
        });
        if (roomCheck?.message?.includes("Room not found")) {
          if (isRoomFallbackEnabled())
            await requestRoomVerification({ lang, invalid: true });
          else
            await router.push({
              name: "page_error_scan",
              query: { lang },
            });
          return "failed";
        }
        if (isRoomFallbackEnabled() && roomCheck?.message !== "Room found")
          return "failed";
      }

      const facility = await apiCallWithRetry(authenticateFacilityBooking, {
        token: user.token || "",
        fallbackURL: window.location.href,
        channel: "web",
        username: user.username || "",
        password: user.password || "",
        facilityIdHome: String(router.currentRoute.value.query.id || ""),
        apiKey: config.apikey,
        categoryId,
      });
      const destination = buildFacilityBookingUrl(rawUrl, {
        lang,
        token: facility.token,
        singleActivity: facility.singleActivity,
      });

      window.location.assign(destination);
      return "launched";
    } catch (error) {
      console.error("Unable to open facility booking", error);
      return "failed";
    }
  };

  return { launchFacilityBooking };
}
